import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, ExternalLink, Pencil, Trash2, Upload, Download, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, getList } from "@/lib/api";
import { uploadFile, validateFile } from "@/lib/upload";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import Pager from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  DOC_CATEGORIES, PAGE_SIZE, ALLOWED_UPLOAD_EXTENSIONS, formatBytes, fmtDate, label,
} from "@/lib/constants";

const CAT_COLORS = {
  SOP: "bg-[#F9F3E6] text-[#8A6D2F] border-[#E5C992]",
  technical_sheet: "bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]",
  regulatory_checklist: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
  playbook: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
};

function FileField({ attached, onAttached, onCleared }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = validateFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setProgress(1);
    try {
      const result = await uploadFile(file, setProgress);
      onAttached(result);
      toast.success(`${result.original_filename} uploaded`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div data-testid="document-file-field">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={pick}
        accept={ALLOWED_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(",")}
        data-testid="document-file-input"
      />
      {attached ? (
        <div className="flex items-center justify-between gap-3 border border-[#A7F3D0] bg-[#ECFDF5] rounded-lg px-4 py-3" data-testid="document-file-attached">
          <div className="flex items-center gap-2 min-w-0">
            <Paperclip size={15} className="text-[#065F46] shrink-0" />
            <span className="text-sm text-[#065F46] font-medium truncate">{attached.original_filename}</span>
            <span className="text-xs text-[#047857] shrink-0">{formatBytes(attached.size)}</span>
          </div>
          <button type="button" onClick={onCleared} className="text-[#065F46] hover:text-[#B91C1C] shrink-0" data-testid="document-file-clear">
            <X size={15} />
          </button>
        </div>
      ) : progress != null ? (
        <div className="border border-[#E8DFD1] rounded-lg px-4 py-3" data-testid="document-upload-progress">
          <div className="flex justify-between text-xs text-[#5C4A3E] mb-2">
            <span>Uploading…</span>
            <span className="font-semibold">{progress}%</span>
          </div>
          <div className="h-1.5 bg-[#F4EFEA] rounded-full overflow-hidden">
            <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border border-dashed border-[#D8CBB8] hover:border-[#C5A059] bg-[#FDFBF7] rounded-lg px-4 py-6 text-center transition-colors"
          data-testid="document-upload-button"
        >
          <Upload size={18} className="text-[#C5A059] mx-auto mb-2" />
          <div className="text-sm font-medium text-[#2A1810]">Upload a file</div>
          <div className="text-xs text-[#8C7A6B] mt-1">PDF, Word, Excel, PowerPoint, CSV or image · up to 25 MB</div>
        </button>
      )}
    </div>
  );
}

function DocumentForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    category: initial?.category || "SOP",
    storage_url: initial?.storage_url || "",
    description: initial?.description || "",
    file_id: initial?.file_id || null,
  });
  const [attached, setAttached] = useState(
    initial?.file ? { ...initial.file, file_id: initial.file_id } : null
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_id && !form.storage_url) {
      toast.error("Upload a file or paste a link");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/documents/${initial.id}`, form);
        toast.success("Document updated");
      } else {
        await api.post("/documents", form);
        toast.success("Document added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial?.id ? "Edit Document" : "New Document"} onClose={onClose} testid="document-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="document-form">
        <Field label="Title">
          <input data-testid="document-title-input" required value={form.title} onChange={set("title")} className={inputCls} />
        </Field>
        <Field label="Category">
          <select data-testid="document-category-select" value={form.category} onChange={set("category")} className={inputCls}>
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
          </select>
        </Field>
        <Field label="File">
          <FileField
            attached={attached}
            onAttached={(result) => { setAttached(result); setForm((f) => ({ ...f, file_id: result.file_id })); }}
            onCleared={() => { setAttached(null); setForm((f) => ({ ...f, file_id: null })); }}
          />
        </Field>
        <Field label="…or an external link">
          <input data-testid="document-url-input" type="url" value={form.storage_url} onChange={set("storage_url")} className={inputCls} placeholder="https://…" />
        </Field>
        <Field label="Description">
          <textarea data-testid="document-description-input" rows={3} value={form.description} onChange={set("description")} className={inputCls} />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="document-cancel-button">Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="document-save-button">
            {saving ? "Saving…" : "Save document"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function Documents() {
  const { canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState("");
  const [offset, setOffset] = useState(0);
  const [modal, setModal] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["documents", category, offset],
    queryFn: () => getList("/documents", { category, limit: PAGE_SIZE, offset }),
  });

  const docs = data?.items ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["documents"] });

  const del = async (doc) => {
    const ok = await confirm({
      title: "Delete document",
      message: `Delete "${doc.title}"?${doc.file ? " The uploaded file will no longer be accessible." : ""}`,
      confirmLabel: "Delete document",
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      setModal(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const download = async (doc) => {
    setDownloading(true);
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.file?.original_filename || doc.title);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDownloading(false);
    }
  };

  const detail = modal?.type === "detail" ? docs.find((d) => d.id === modal.initial.id) || modal.initial : null;

  return (
    <div data-testid="documents-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-[#E8DFD1]">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Templates & SOPs</h1>
          <p className="text-sm text-[#8C7A6B] mt-1">Standard procedures, technical sheets and playbooks.</p>
        </div>
        {canWrite && (
          <button onClick={() => setModal({ type: "form" })} className={btnPrimary} data-testid="new-document-button">
            <Plus size={16} /> Add Document
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="document-category-filters">
        <button
          onClick={() => { setOffset(0); setCategory(""); }}
          data-testid="doc-chip-all"
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!category ? "bg-[#2A1810] text-[#FDFBF7] border-[#2A1810]" : "bg-white text-[#5C4A3E] border-[#E8DFD1] hover:border-[#C5A059]"}`}
        >
          All
        </button>
        {DOC_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => { setOffset(0); setCategory(c === category ? "" : c); }}
            data-testid={`doc-chip-${c}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${category === c ? "bg-[#2A1810] text-[#FDFBF7] border-[#2A1810]" : "bg-white text-[#5C4A3E] border-[#E8DFD1] hover:border-[#C5A059]"}`}
          >
            {label(c)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-[#8C7A6B]">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="documents-grid">
            {docs.map((d) => (
              <button
                key={d.id}
                onClick={() => setModal({ type: "detail", initial: d })}
                className="text-left bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm hover:border-[#C5A059] hover:shadow-md transition-all"
                data-testid={`document-card-${d.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <FileText size={18} className="text-[#C5A059] shrink-0 mt-0.5" />
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${CAT_COLORS[d.category] || CAT_COLORS.SOP}`}>
                    {label(d.category)}
                  </span>
                </div>
                <h3 className="font-semibold text-[#2A1810] text-sm mb-2">{d.title}</h3>
                <p className="text-xs text-[#8C7A6B] leading-relaxed line-clamp-2">{d.description}</p>
                {d.file && (
                  <div className="flex items-center gap-1.5 mt-3 text-[10px] text-[#065F46] font-semibold" data-testid={`document-file-badge-${d.id}`}>
                    <Paperclip size={11} /> {d.file.original_filename} · {formatBytes(d.file.size)}
                  </div>
                )}
                <div className="text-[10px] text-[#8C7A6B] mt-3 pt-3 border-t border-[#F4EFEA]">
                  {d.created_by_name} · {fmtDate(d.created_at)}
                </div>
              </button>
            ))}
            {docs.length === 0 && (
              <div className="col-span-full text-center text-[#8C7A6B] text-sm py-12" data-testid="documents-empty">No documents in this category.</div>
            )}
          </div>
          <Pager offset={offset} limit={PAGE_SIZE} total={data?.total ?? 0} onChange={setOffset} testid="documents-pager" />
        </>
      )}

      {modal?.type === "form" && <DocumentForm initial={modal.initial} onClose={() => setModal(null)} onSaved={refresh} />}

      {detail && (
        <Modal title={detail.title} onClose={() => setModal(null)} testid="document-detail-modal">
          <div className="space-y-4" data-testid="document-detail">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${CAT_COLORS[detail.category] || CAT_COLORS.SOP}`}>
                {label(detail.category)}
              </span>
            </div>
            <p className="text-sm text-[#5C4A3E] leading-relaxed">{detail.description}</p>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">Added by</span><span className="text-[#2A1810] font-medium">{detail.created_by_name}</span></div>
              <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">Created</span><span className="text-[#2A1810] font-medium">{fmtDate(detail.created_at)}</span></div>
              {detail.file && (
                <>
                  <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">File</span><span className="text-[#2A1810] font-medium break-all">{detail.file.original_filename}</span></div>
                  <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">Size</span><span className="text-[#2A1810] font-medium">{formatBytes(detail.file.size)}</span></div>
                </>
              )}
            </div>
            {detail.file ? (
              <button
                onClick={() => download(detail)}
                disabled={downloading}
                className={`${btnPrimary} w-full justify-center`}
                data-testid="download-document-button"
              >
                <Download size={15} /> {downloading ? "Preparing…" : "Download file"}
              </button>
            ) : null}
            {detail.storage_url ? (
              <a
                href={detail.storage_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${detail.file ? btnSecondary : btnPrimary} w-full justify-center flex items-center gap-2`}
                data-testid="open-document-link"
              >
                <ExternalLink size={15} /> Open link
              </a>
            ) : null}
            {canWrite && (
              <div className="flex justify-end gap-2 pt-2 border-t border-[#F4EFEA]">
                <button onClick={() => setModal({ type: "form", initial: detail })} className={btnSecondary} data-testid="edit-document-button"><Pencil size={13} /> Edit</button>
                <button onClick={() => del(detail)} className="bg-[#FEF2F2] hover:bg-[#FEE2E2] text-[#B91C1C] border border-[#FECACA] px-4 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2" data-testid="delete-document-button">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
